import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import ChangeFactorTable from "../../Components/ChangeFactorTable/ChangeFactorTable";
import MenuTitle from "../../Components/MenuTitlle/MenuTitle";
import ChangeDetailProduct from "../../Components/ChangeDetailProduct/ChangeDetailProduct";
import ChangeProduct from "../../Components/ChangeProduct/ChangeProduct";
import Style from "./ChangeFactor.module.css";
import "../ManageCustomer/ManageCustomer.css";
import {
  changeFactorListPostApi,
  getProductDetailApi,
} from "../../Api/BarestSupplierServices";
import { orderListDetailApi } from "../../Api/SupplierServices";
import { separate } from "../../Functions/NumberSeprator";
import { errorToast, succToast } from "../../Utils/Toast/Toast";
import {
  ExclamationTriangleFill,
  CheckCircleFill,
  PencilSquare,
  ArrowRepeat,
  ArrowRight,
} from "react-bootstrap-icons";

const ChangeFactor = (props) => {
  const orderId = props.location?.state;

  // Modal Visibility
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openChangeProductModal, setOpenChangeProductModal] = useState(false);

  // Active item being edited
  const [selectedItem, setSelectedItem] = useState(null);

  // Inventory products cache & loading
  const [inventoryProducts, setInventoryProducts] = useState(null);
  const [isInventoryLoading, setIsInventoryLoading] = useState(false);

  // State
  const [disable, setDisable] = useState(false);
  const [orderListDetails, setOrderListDetails] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);

  // Fetch order items on mount
  const getOrderListsDetail = useCallback(async () => {
    if (!orderId) return;
    setIsLoadingOrder(true);
    try {
      const data = await orderListDetailApi(orderId);
      if (data && Array.isArray(data.products)) {
        setOrderListDetails(data.products);
      }
    } catch (err) {
      errorToast("خطا در دریافت جزئیات فاکتور");
    } finally {
      setIsLoadingOrder(false);
    }
  }, [orderId]);

  useEffect(() => {
    getOrderListsDetail();
  }, [getOrderListsDetail]);

  // Fetch inventory products once or on demand
  const loadInventory = useCallback(() => {
    if (inventoryProducts) return;
    setIsInventoryLoading(true);
    const info = {
      exist: 1,
      category_id: 0,
    };
    getProductDetailApi(info, (res) => {
      setIsInventoryLoading(false);
      if (res && res.products) {
        setInventoryProducts(res.products);
      }
    });
  }, [inventoryProducts]);

  // Handle table action buttons
  const btnHandler = (actionType, record) => {
    setSelectedItem(record);

    if (actionType === 1) {
      // Edit characteristics (price, count, availability)
      setOpenDetailModal(true);
    } else if (actionType === 2) {
      // Replace with another product from inventory
      loadInventory();
      setOpenChangeProductModal(true);
    }
  };

  const handleClose = () => {
    setOpenDetailModal(false);
    setOpenChangeProductModal(false);
    setSelectedItem(null);
  };

  // Upsert updated details into state
  const handleUpdateProductDetail = (updatedData) => {
    // 1. Update allProducts (payload for API)
    setAllProducts((prev) => {
      const existingIdx = prev.findIndex(
        (item) => item.product_id === updatedData.product_id
      );
      if (existingIdx > -1) {
        const next = [...prev];
        next[existingIdx] = updatedData;
        return next;
      }
      return [...prev, updatedData];
    });

    // 2. Update orderListDetails locally for immediate UI feedback
    setOrderListDetails((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) => {
        if (item.id === updatedData.product_id) {
          return {
            ...item,
            new_price: updatedData.new_price,
            new_count: updatedData.new_count,
            availability: String(updatedData.availability),
            isModified: true,
          };
        }
        return item;
      });
    });

    succToast("تغییرات کالا در پیش‌نویس فاکتور اعمال شد.");
  };

  // Upsert product replacement into state
  const handleReplaceProduct = (replacementData) => {
    const payload = {
      product_id: replacementData.product_id,
      p_product_id: replacementData.p_product_id,
      new_count: replacementData.new_count,
      new_price: replacementData.new_price,
      availability: replacementData.availability,
    };

    // 1. Update allProducts
    setAllProducts((prev) => {
      const existingIdx = prev.findIndex(
        (item) => item.p_product_id === replacementData.p_product_id
      );
      if (existingIdx > -1) {
        const next = [...prev];
        next[existingIdx] = payload;
        return next;
      }
      return [...prev, payload];
    });

    // 2. Update orderListDetails locally for immediate UI feedback
    setOrderListDetails((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) => {
        if (item.id === replacementData.p_product_id) {
          return {
            ...item,
            name: `${replacementData.selectedProduct?.name || "کالای جایگزین"} (جایگزین ${item.name})`,
            new_count: replacementData.new_count,
            new_price: replacementData.new_price,
            availability: "1",
            p_product_id: item.id,
            isModified: true,
          };
        }
        return item;
      });
    });

    succToast("کالای جایگزین در پیش‌نویس فاکتور قرار گرفت.");
  };

  // Submit changes to backend
  const changeFactorList = () => {
    if (allProducts.length === 0) {
      errorToast("تغییری در فاکتور اعمال نشده است.");
      return;
    }

    setDisable(true);
    const info = {
      id: orderId,
      products: allProducts,
    };

    changeFactorListPostApi(info, (res) => {
      setDisable(false);
      if (res && res.status === 1) {
        succToast("فاکتور با موفقیت ویرایش و ذخیره شد.");
        setAllProducts([]);
        getOrderListsDetail();
        // Go back after successful update
        if (props.history) {
          setTimeout(() => {
            props.history.goBack();
          }, 1200);
        }
      } else {
        errorToast((res && res.message) || "خطا در ویرایش فاکتور");
      }
    });
  };

  // Prepare table data with raw and formatted values
  const tableData =
    orderListDetails &&
    orderListDetails.map((item, index) => {
      return {
        key: item.id || index,
        id: item.id,
        name: item.name,
        count: item.count,
        newCount: item.new_count !== undefined ? item.new_count : item.count,
        price: separate(item.price),
        newPrice: separate(item.new_price !== undefined ? item.new_price : item.price),
        rawPrice: item.price,
        rawNewPrice: item.new_price,
        pProductId: item.p_product_id,
        available: item.availability,
        isModified: Boolean(item.isModified),
      };
    });

  return (
    <div className={Style.pageHolder}>
      <MenuTitle title="ویرایش فاکتور سفارش" />

      <div className={Style.contentContainer}>
        {/* Important Notice Card */}
        <div className={Style.noticeCard}>
          <div className={Style.noticeIconHolder}>
            <ExclamationTriangleFill />
          </div>
          <div className={Style.noticeTextGroup}>
            <h4 className={Style.noticeTitle}>نکات مهم قبل از ثبت تغییرات فاکتور</h4>
            <p className={Style.noticeDescription}>
              تنها <strong>یک‌بار</strong> مجاز به تغییر فاکتور می‌باشید. پس از
              ثبت، فاکتور اصلاح‌شده به مشتری نمایش داده می‌شود. لطفاً قیمت‌ها و
              تعداد را به دقت بازبینی فرمایید.
            </p>
          </div>
        </div>

        {/* Change Factor Table */}
        <ChangeFactorTable
          btnHandler={btnHandler}
          orderId={orderId}
          data={isLoadingOrder ? undefined : tableData}
        />

        {/* Bottom Submission Toolbar */}
        <div className={Style.bottomBar}>
          <div className={Style.changesSummary}>
            {allProducts.length > 0 ? (
              <span className={Style.changesActive}>
                <CheckCircleFill /> {allProducts.length} مورد تغییر آماده ثبت
              </span>
            ) : (
              <span className={Style.changesEmpty}>
                هنوز تغییری اعمال نشده است
              </span>
            )}
          </div>

          <div className={Style.buttonGroup}>
            {props.history && (
              <button
                type="button"
                className={Style.backBtn}
                onClick={() => props.history.goBack()}
              >
                <ArrowRight />
                <span>بازگشت</span>
              </button>
            )}

            <button
              disabled={disable || allProducts.length === 0}
              className={Style.submitBtn}
              onClick={changeFactorList}
            >
              {disable ? (
                <div className={Style.btnSpinner}></div>
              ) : (
                <span>ثبت نهایی تغییرات فاکتور</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modal 1: Edit Product Characteristics */}
      <Modal
        show={openDetailModal}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName={Style.modalDialog}
      >
        <Modal.Header closeButton className={Style.modalHeader}>
          <Modal.Title className={Style.modalTitle}>
            <PencilSquare className={Style.modalTitleIcon} />
            <span>ویرایش مشخصات کالا</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={Style.modalBody}>
          {selectedItem && (
            <ChangeDetailProduct
              rawInfo={selectedItem}
              handleClose={handleClose}
              sendData={handleUpdateProductDetail}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Modal 2: Replace Product from Inventory */}
      <Modal
        show={openChangeProductModal}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName={Style.modalDialog}
      >
        <Modal.Header closeButton className={Style.modalHeader}>
          <Modal.Title className={Style.modalTitle}>
            <ArrowRepeat className={Style.modalTitleIcon} />
            <span>جایگزینی کالا از انبار</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={Style.modalBody}>
          {selectedItem && (
            <ChangeProduct
              handleClose={handleClose}
              searchValue={inventoryProducts}
              isLoading={isInventoryLoading}
              rawInfo={selectedItem}
              setChangeProduct={handleReplaceProduct}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ChangeFactor;
